import React, { useEffect } from 'react';
import { Case } from '../types';

interface BasicImageViewerProps {
  images: Case['images'];
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
}

export function BasicImageViewer({ images, currentIndex, onClose, onNext, onPrevious }: BasicImageViewerProps) {
  const currentImage = images?.[currentIndex];

  const IMAGE_TYPE_LABELS: Record<string, string> = {
    'he': 'Hematoxylin/Eosin (H&E)',
    'histochemical': 'Ιστοχημικές χρώσεις',
    'immunohistochemistry': 'Ανοσοϊστοχημεία',
    'molecular': 'Μοριακή',
    'gross': 'Μακροσκοπική'
  };

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrevious();
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [onClose, onNext, onPrevious]);

  if (!currentImage) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
      <div className="relative w-full h-full flex items-center justify-center p-4">
        {/* Image Label - Top Left */}
        {(currentImage.type || currentImage.imageType) && (
          <div className="absolute top-4 left-4 z-10">
            <div className="bg-black bg-opacity-80 text-white px-4 py-3 rounded-lg font-medium text-base backdrop-blur-sm border border-white/30 shadow-lg">
              <div className="font-semibold text-blue-300 mb-1">
                {IMAGE_TYPE_LABELS[currentImage.type || currentImage.imageType] || currentImage.type || currentImage.imageType}
              </div>
              
              {/* Display staining information for immunohistochemistry and histochemical */}
              {(currentImage.staining || currentImage.stainingInfo) && (
                (currentImage.type === 'immunohistochemistry' || currentImage.type === 'histochemical') && (
                  <div className="text-sm text-yellow-200 font-medium mt-2 bg-black bg-opacity-50 px-2 py-1 rounded">
                    🔬 Χρώση: {currentImage.staining || currentImage.stainingInfo}
                  </div>
                )
              )}
              
              {/* Display magnification information for all images */}
              {(currentImage.magnification) && (
                <div className="text-sm text-green-200 font-medium mt-2 bg-black bg-opacity-50 px-2 py-1 rounded">
                  🔍 Μεγέθυνση: {currentImage.magnification}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300 text-2xl z-10 bg-black bg-opacity-50 rounded-full w-12 h-12 flex items-center justify-center touch-manipulation min-h-[44px] min-w-[44px] backdrop-blur-sm"
        >
          ✕
        </button>

        {/* Previous button */}
        {images && images.length > 1 && (
          <button
            onClick={onPrevious}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 text-3xl z-10 bg-black bg-opacity-50 rounded-full w-14 h-14 flex items-center justify-center touch-manipulation min-h-[44px] min-w-[44px] backdrop-blur-sm"
          >
            ‹
          </button>
        )}

        {/* Next button */}
        {images && images.length > 1 && (
          <button
            onClick={onNext}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 text-3xl z-10 bg-black bg-opacity-50 rounded-full w-14 h-14 flex items-center justify-center touch-manipulation min-h-[44px] min-w-[44px] backdrop-blur-sm"
          >
            ›
          </button>
        )}

        {/* Image */}
        <img
          src={currentImage.imageUrl}
          alt={currentImage.caption || `Image ${currentIndex + 1}`}
          className="max-w-full max-h-full object-contain"
        />

        {/* Image info */}
        <div className="absolute bottom-4 left-4 right-4 text-white text-center">
          <p className="text-lg font-medium mb-2">
            {currentImage.caption || `Εικόνα ${currentIndex + 1}`}
          </p>
          {images && images.length > 1 && (
            <p className="text-sm text-gray-300">
              {currentIndex + 1} από {images.length}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}